import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectors as authSelectors } from 'store/auth';
import {
  Box,
  Button,
  Card,
  FormDialog,
  Icon,
  InputField,
  MenuItem,
  MuiBox,
  MuiTable,
  OutlinedInput,
  Select,
  SelectDropdown,
  Switch,
  Tooltip,
  Typography,
  SvgIconButton,
  SvgIcon,
  Chip
} from '@kis-technologies/kis-ui';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { actions as hardwareActions, selectors as hardwareSelectors } from 'store/hardware';
import {
  selectors as kioskTicketTemplateSelectors,
  actions as kioskTicketTemplateActions
} from 'store/kioskTicketTemplates';
import { selectors as venuesSelectors, actions as venuesActions } from 'store/venues';
import { selectors as ciSelectors, actions as ciActions } from 'store/clientIntegrations';
import { selectors as configSelectors, actions as configActions } from 'store/configurations';
import { selectors as areasSelectors, actions as areasActions } from 'store/areas';
import {
  TicketTemplate,
  selectors as ticketTemplatesSelectors,
  actions as ticketTemplatesActions
} from 'store/ticketTemplates';
import { getSortedList } from 'utils/stringFormat';
import { useActions } from 'hooks/useActions';
import { INVALID, VALID } from '../../constants';
import {
  FormSelectProps,
  KioskFormData,
  KioskFormOverlayProps,
  KioskHardwareItem,
  KioskHardwareFormData,
  Mode
} from '../../types';
import { kioskValidationSchema, getHasEmpty, getHasInvalidInput } from '../../utils';
import { KioskAreaOverlay } from './KioskAreaOverlay';
import { KioskHardwareOverlay } from './KioskHardwareOverlay';
import { KioskHardwareForm } from './KioskHardwareOverlay/KioskHardwareForm';
import { KioskTemplateOverlay } from './KioskTemplateOverlay';
import { KioskVenueOverlay } from './KioskVenueOverlay';
import { useKioskFormStyles } from './styled';

const defaultKioskFormData: KioskFormData = {
  name: '',
  serialNumber: '',
  ipAddress: '',
  macAddress: '',
  venue: '',
  area: '',
  configurationId: '',
  clientIntegrationId: '',
  templateId: [],
  hardware: [],
  configuration: {
    showInKCC: false,
    kioskPreview: {
      enabled: false,
      allowCheckOut: false
    }
  }
};

const FormSelect: React.FC<FormSelectProps> = ({
  label,
  items,
  value,
  onChange,
  disabled,
  classes,
  showSettings,
  settingsTooltip,
  onSettingsClick
}) => {
  return (
    <MuiBox>
      <Typography
        display="flex"
        justifyContent={showSettings ? 'space-between' : 'flex-start'}
        className={classes.formDropdownIconHeadLabel}
      >
        {label}

        {showSettings && (
          <MuiBox mr="10px" display="flex">
            <Tooltip arrow title={settingsTooltip || ''} placement="top" verticalOffset={-8}>
              <Icon name="Settings" sx={{ cursor: 'pointer' }} onClick={onSettingsClick} />
            </Tooltip>
          </MuiBox>
        )}
      </Typography>

      <SelectDropdown
        placeholder=""
        items={items}
        itemToString={(item) => item?.name || ''}
        selectedItem={items.find((i) => i.id === value) ?? null}
        onChange={(item) => onChange(item?.id ?? '')}
        radius="round"
        width="262px"
        disabled={disabled}
        sx={{
          boxShadow: 'none',
          height: '45px',
          '& .MuiSelect-select': {
            color: value ? '#000' : '#9E9E9E'
          }
        }}
      />
    </MuiBox>
  );
};

export const KioskFormOverlay: React.FC<KioskFormOverlayProps> = (props: KioskFormOverlayProps) => {
  const { open, mode, initialData, onClose, onSuccess } = props;

  const classes = useKioskFormStyles();

  const [formData, setFormData] = useState<KioskFormData>(defaultKioskFormData);
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});
  const [validation, setValidation] = useState<Record<string, string>>({});
  const [templateOverlayOpen, setTemplateOverlayOpen] = useState(false);
  const [areaOverlayOpen, setAreaOverlayOpen] = useState(false);
  const [venueOverlayOpen, setVenueOverlayOpen] = useState(false);
  const [hardwareOverlayOpen, setHardwareOverlayOpen] = useState(false);
  const [hardwareFormOpen, setHardwareFormOpen] = useState(false);
  const [hardwareMode, setHardwareMode] = useState<Mode>('create');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedHardwareToDelete, setSelectedHardwareToDelete] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedHardware, setSelectedHardware] = useState<any | null>(null);
  const [hardwareSortOrder, setHardwareSortOrder] = useState<'asc' | 'desc'>('asc');

  const [getKioskTicketTemplates, resetKioskTicketTemplates] = useActions([
    kioskTicketTemplateActions.getKioskTicketTemplates,
    kioskTicketTemplateActions.resetKioskTicketTemplates
  ]);

  const admin = useSelector(authSelectors.selectAdmin);
  const areas = useSelector(areasSelectors.selectAreas);
  const hardware = useSelector(hardwareSelectors.selectHardware);
  const venues = useSelector(venuesSelectors.selectVenues);
  const clientIntegrations = useSelector(ciSelectors.selectClientIntegrations);
  const configurations = useSelector(configSelectors.selectConfigurations);
  const kioskTicketTemplates = useSelector(kioskTicketTemplateSelectors.selectKioskTicketTemplates);
  const templates = useSelector(ticketTemplatesSelectors.selectTicketTemplates);
  const sortedVenues = getSortedList(venues, 'name');
  const sortedCIs = getSortedList(clientIntegrations, 'name');
  const sortedConfigs = getSortedList(configurations, 'name');
  const sortedAreas = getSortedList(areas, 'name');
  const filteredAreas = sortedAreas.filter((a) => a.venueId === formData.venue);
  const sortedTemplates = getSortedList(templates, 'type', 'id');

  const [getVenues] = useActions([venuesActions.getVenues]);
  const [getAreas] = useActions([areasActions.getAreas]);
  const [getCIs] = useActions([ciActions.getClientIntegrations]);
  const [getConfigs] = useActions([configActions.getConfigurations]);
  const [getTicketTemplates] = useActions([ticketTemplatesActions.getTicketTemplates]);
  const [getHardware] = useActions([hardwareActions.getHardware]);
  const client = useSelector(clientsSelectors.selectClient) as Client;

  const isEdit = mode === 'edit';
  const role = admin?.role;
  const isAdmin = role === 'ClientAdmin';
  const isGlobalAdmin = role === 'GlobalAdmin';
  const canEditAdminAndGA = isAdmin || isGlobalAdmin;
  const canEditGA = isGlobalAdmin;

  const loadKioskDependencies = () => {
    getVenues();
    getCIs();
    getConfigs();
    getHardware();
    getTicketTemplates();
    getAreas();
  };

  // Sorting hardware based on name
  const sortedHardware = useMemo(() => {
    if (!formData.hardware?.length) return [];

    const sorted = getSortedList(formData.hardware, 'name');
    return hardwareSortOrder === 'asc' ? sorted : [...sorted].reverse();
  }, [formData.hardware, hardwareSortOrder]);

  useEffect(() => {
    if (!client) return;
    loadKioskDependencies();
  }, [client]);

  useEffect(() => {
    if (!open) {
      resetKioskTicketTemplates();
    }
  }, [open]);

  useEffect(() => {
    if (isEdit && initialData?.id) {
      resetKioskTicketTemplates();
      getKioskTicketTemplates({ kioskId: initialData.id });
    }
  }, [isEdit, initialData]);

  useEffect(() => {
    if (!open) return;

    if (isEdit && initialData) {
      setFormData({
        ...defaultKioskFormData,
        name: initialData.name ?? '',
        serialNumber: initialData.serialNumber ?? '',
        ipAddress: initialData.ipAddress ?? '',
        macAddress: initialData.macAddress ?? '',
        venue: initialData.venue?.id ?? '',
        area: initialData.areaId ?? '',
        configurationId: initialData.configurationId ?? '',
        clientIntegrationId: initialData.clientIntegrationId ?? '',
        hardware: initialData.hardware,
        configuration: {
          showInKCC: initialData.configuration?.showInKCC ?? false,
          kioskPreview: {
            enabled: initialData.configuration?.kioskPreview?.enabled ?? false,
            allowCheckOut: initialData.configuration?.kioskPreview?.allowCheckOut ?? false
          }
        }
      });
    } else {
      setFormData({
        ...defaultKioskFormData
      });
    }
  }, [open, isEdit, initialData]);

  useEffect(() => {
    if (isEdit) return;
    if (!formData.clientIntegrationId && sortedCIs.length) {
      updateField('clientIntegrationId', sortedCIs[0].id);
    }

    if (!formData.configurationId && sortedConfigs.length) {
      updateField('configurationId', sortedConfigs[0].id);
    }

    if (!formData.venue && sortedVenues.length) {
      updateField('venue', sortedVenues[0].id);
    }
  }, [sortedCIs, sortedConfigs, sortedVenues]);

  useEffect(() => {
    if (isEdit) return;
    if (!formData.area && filteredAreas.length) {
      updateField('area', filteredAreas[0].id);
    }
  }, [filteredAreas]);

  useEffect(() => {
    if (!isEdit || !initialData?.id) return;

    const templateIds = kioskTicketTemplates.filter((t) => t.kioskId === initialData.id).map((t) => t.ticketTemplateId);

    setFormData((prev) => ({
      ...prev,
      templateId: templateIds
    }));
  }, [kioskTicketTemplates, isEdit, initialData]);

  const updateField = <K extends keyof KioskFormData>(key: K, value: KioskFormData[K]) => {
    if (validation[key] === INVALID) {
      setValidation((prev) => ({ ...prev, [key]: VALID }));
      setValidationMessage((prev) => ({ ...prev, [key]: null }));
    }
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const onInputBlur = async (key: keyof typeof formData, value: string | null = null) => {
    const trimmed = value?.trim() || '';
    setFormData((prev) => ({
      ...prev,
      [key]: trimmed
    }));

    let invalidMessage: string | null = null;

    if (trimmed.length) {
      const result = await kioskValidationSchema.validate({ [key]: trimmed }).catch((err) => err);
      invalidMessage = result && result.message;
    }
    setValidation((prev) => ({
      ...prev,
      [key]: invalidMessage ? INVALID : VALID
    }));
    setValidationMessage((prev) => ({
      ...prev,
      [key]: invalidMessage
    }));
  };

  const hasEmptyInput = getHasEmpty([
    formData.name,
    formData.macAddress,
    formData.clientIntegrationId,
    formData.venue,
    formData.area
  ]);

  const hasInvalidInput = getHasInvalidInput(validation);

  const isCreateDisabled = hasEmptyInput || hasInvalidInput;

  const validate = (key: keyof typeof formData) => {
    return validationMessage[key];
  };

  const handleHardwareSubmit = (payload: KioskHardwareFormData) => {
    const hardwareItem = hardware.find((h) => h.id === payload.hardwareId);

    const hardwareData: KioskHardwareItem = {
      id: hardwareMode === 'edit' ? selectedHardware?.id : crypto.randomUUID(),
      hardwareId: payload.hardwareId,
      serialCode: payload.serialCode,
      configuration:
        typeof payload.configuration === 'string' ? JSON.parse(payload.configuration || '{}') : payload.configuration,
      name: hardwareItem?.name,
      type: hardwareItem?.type,
      values: hardwareMode === 'edit' ? selectedHardware?.values : undefined
    };

    setFormData((prev) => {
      if (hardwareMode === 'edit') {
        return {
          ...prev,
          hardware: prev.hardware.map((hw) => (hw.id === hardwareData.id ? hardwareData : hw))
        };
      }

      return {
        ...prev,
        hardware: [...prev.hardware, hardwareData]
      };
    });

    setHardwareFormOpen(false);
  };

  const handleSubmit = () => {
    onSuccess(formData);
  };

  const SortArrow = ({ active }: { active: boolean }) =>
    active ? (
      <SvgIcon name={hardwareSortOrder === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
    ) : null;

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Edit Kiosk' : 'Create Kiosk'}
      cancelButtonText="Cancel"
      confirmButtonText={isEdit ? 'Save' : 'Create'}
      cancelButtonAction={onClose}
      confirmButtonAction={handleSubmit}
      confirmButtonDisabled={isCreateDisabled}
    >
      <MuiBox className={classes.formInnerContainer}>
        <MuiBox className={classes.formInputBox}>
          {/* Name section */}
          <MuiBox>
            <InputField
              label="Name"
              size="small"
              fullWidth
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              onBlur={(e) => onInputBlur('name', e.target.value)}
              helperText={validate('name')}
              error={validation.name === INVALID}
              sx={{ width: '262px' }}
              disabled={isEdit && !canEditAdminAndGA}
            />
          </MuiBox>
          {/* Serial number section */}
          <MuiBox>
            <InputField
              label="Serial Number"
              size="small"
              sx={{ width: '262px' }}
              fullWidth
              disabled={isEdit && !canEditGA}
              value={formData.serialNumber}
              onBlur={(e) => onInputBlur('serialNumber', e.target.value)}
              helperText={validate('serialNumber')}
              error={validation.serialNumber === INVALID}
              onChange={(e) => updateField('serialNumber', e.target.value)}
            />
          </MuiBox>
        </MuiBox>
        <MuiBox className={classes.formInputBox}>
          {/* MAC Address section */}
          <MuiBox>
            <InputField
              label="MAC Address"
              size="small"
              sx={{ width: '262px' }}
              fullWidth
              placeholder="00:00:00:00:00:00"
              value={formData.macAddress}
              onChange={(e) => updateField('macAddress', e.target.value)}
              helperText={validate('macAddress')}
              error={validation.macAddress === INVALID}
              disabled={isEdit && !canEditGA}
            />
          </MuiBox>
          {/* IP Address section */}
          <MuiBox>
            <InputField
              label="IP Address"
              size="small"
              sx={{ width: '262px' }}
              fullWidth
              value={formData.ipAddress}
              disabled
            />
          </MuiBox>
        </MuiBox>
        <MuiBox className={classes.formInputBox}>
          {/* Environment section */}
          <FormSelect
            label="Environment"
            items={sortedCIs}
            value={formData.clientIntegrationId}
            onChange={(id) => updateField('clientIntegrationId', id)}
            disabled={isEdit && !canEditAdminAndGA}
            classes={classes}
          />
          {/* Configuration section */}
          <FormSelect
            label="Configuration"
            items={sortedConfigs}
            value={formData.configurationId}
            onChange={(id) => updateField('configurationId', id)}
            disabled={isEdit && !canEditGA}
            classes={classes}
          />
        </MuiBox>
        {/* Venue section */}
        <MuiBox className={classes.formInputBox}>
          <FormSelect
            label="Venue"
            items={sortedVenues}
            value={formData.venue}
            onChange={(id) => {
              updateField('venue', id);
              updateField('area', '');
            }}
            classes={classes}
            showSettings={canEditAdminAndGA}
            settingsTooltip="Manage Venues"
            onSettingsClick={() => {
              setVenueOverlayOpen(true);
            }}
            disabled={isEdit && !canEditAdminAndGA}
          />
          {/* Area section */}
          <FormSelect
            label="Area"
            items={filteredAreas}
            value={formData.area}
            onChange={(id) => updateField('area', id)}
            classes={classes}
            disabled={(isEdit && !canEditAdminAndGA) || !formData.venue}
            showSettings={canEditAdminAndGA}
            settingsTooltip="Manage Areas"
            onSettingsClick={() => {
              if (!formData.venue) return;
              setAreaOverlayOpen(true);
            }}
          />
        </MuiBox>
        <MuiBox
          sx={{
            display: 'grid',
            gridTemplateColumns: '262px 262px',
            columnGap: '1rem',
            width: '100%'
          }}
        >
          {/* Templates section */}
          <Typography className={classes.formDropdownIconHeadLabel}>Templates</Typography>
          <MuiBox display="flex" justifyContent="flex-end" alignItems="center" height="20px">
            {canEditAdminAndGA && (
              <MuiBox mr="10px" display="flex">
                <Tooltip arrow title="Manage Templates" placement="top" verticalOffset={-8}>
                  <Icon
                    name="Settings"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setTemplateOverlayOpen(true);
                    }}
                  />
                </Tooltip>
              </MuiBox>
            )}
          </MuiBox>
          <Select
            className={classes.filtersMultiSelect}
            multiple
            displayEmpty
            value={formData.templateId}
            onChange={(e) => updateField('templateId', e.target.value as string[])}
            input={<OutlinedInput />}
            renderValue={(value) => {
              const selected = value as string[];
              if (selected.length === 0) {
                return <Typography>Select Template(s)</Typography>;
              }
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const template = sortedTemplates.find((t) => t.id === id);
                    return <Chip key={id} label={template ? `${template.name}` : ''} size="small" />;
                  })}
                </Box>
              );
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              PaperProps: {
                sx: {
                  mt: 2,
                  maxHeight: 250,
                  overflowY: 'auto',
                  borderRadius: '15px'
                }
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
            sx={{
              gridColumn: '1 / span 2',
              width: '540px'
            }}
            disabled={isEdit && !canEditAdminAndGA}
          >
            {sortedTemplates.map((template: TicketTemplate) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name} ({template.type})
              </MenuItem>
            ))}
          </Select>
        </MuiBox>
        <MuiBox mt="42px" width="100%">
          {/* Hardware section */}
          <MuiBox display="flex" justifyContent="space-between" alignItems="flex-end" mb={1}>
            <Typography className={classes.formDropdownLabel} sx={{ margin: '0px 0px 2px !important' }}>
              Hardware
            </Typography>
            <MuiBox
              display="flex"
              gap="10px"
              width="50%"
              justifyContent="end"
              alignItems="flex-end"
              paddingRight="10px"
            >
              {canEditGA && (
                <Button
                  customSize="small"
                  onClick={() => {
                    setHardwareMode('create');
                    setSelectedHardware(null);
                    setHardwareFormOpen(true);
                  }}
                >
                  Add Hardware
                </Button>
              )}
              {canEditGA && (
                <Tooltip arrow title="Manage Hardware" placement="top" verticalOffset={-8}>
                  <Icon
                    name="Settings"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setHardwareOverlayOpen(true);
                    }}
                  />
                </Tooltip>
              )}
            </MuiBox>
          </MuiBox>
          <MuiBox className={classes.kioskPopUpTableOuterBox}>
            <Card hasBorderRadius raised fullHeight>
              <Box flexOverride="1">
                <MuiTable.TableContainer className={classes.kioskPopUpTableContainer} id="kioskDetailsTable">
                  <MuiTable.Table stickyHeader>
                    <MuiTable.TableHead>
                      <MuiTable.TableRow>
                        <MuiTable.TableCell
                          className={classes.kioskDetailsHeaders}
                          sx={{ width: '220px', paddingLeft: '24px', whiteSpace: 'nowrap' }}
                          onClick={() => setHardwareSortOrder(hardwareSortOrder === 'asc' ? 'desc' : 'asc')}
                        >
                          Hardware <SortArrow active />
                        </MuiTable.TableCell>
                        {canEditGA && (
                          <MuiTable.TableCell
                            className={classes.kioskDetailsHeaders}
                            sx={{ cursor: 'default', textAlign: 'center', width: '25%' }}
                          >
                            Actions
                          </MuiTable.TableCell>
                        )}
                      </MuiTable.TableRow>
                    </MuiTable.TableHead>
                    <MuiTable.TableBody>
                      {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (sortedHardware || []).map((hw) => (
                          <MuiTable.TableRow key={hw.id}>
                            <MuiTable.TableCell className={classes.tableFirstCell}>{hw.name}</MuiTable.TableCell>
                            {canEditGA && sortedHardware.length > 0 && (
                              <MuiTable.TableCell className={classes.tableActionsCell}>
                                <MuiBox className="actionsContainer">
                                  <Tooltip title="Edit Kiosk Hardware" arrow placement="top" verticalOffset={-8}>
                                    <SvgIconButton
                                      onClick={() => {
                                        setHardwareMode('edit');
                                        setSelectedHardware(hw);
                                        setHardwareFormOpen(true);
                                      }}
                                    >
                                      <SvgIcon name="edit_circle" />
                                    </SvgIconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete Kiosk Hardware" arrow placement="top" verticalOffset={-8}>
                                    <SvgIconButton
                                      onClick={() => {
                                        setSelectedHardwareToDelete(hw);
                                        setDeleteConfirmOpen(true);
                                      }}
                                    >
                                      <SvgIcon name="exit_circle" />
                                    </SvgIconButton>
                                  </Tooltip>
                                </MuiBox>
                              </MuiTable.TableCell>
                            )}
                          </MuiTable.TableRow>
                        ))
                      }
                    </MuiTable.TableBody>
                  </MuiTable.Table>
                </MuiTable.TableContainer>
              </Box>
            </Card>
          </MuiBox>
        </MuiBox>
        {isGlobalAdmin && (
          <MuiBox sx={{ height: '120px', width: '100%', marginTop: '2.3rem' }}>
            <Switch
              checked={formData.configuration.kioskPreview?.enabled}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  configuration: {
                    ...prev.configuration,
                    kioskPreview: {
                      ...prev.configuration.kioskPreview,
                      enabled: e.target.checked
                    }
                  }
                }))
              }
              checkedLabel="Allow this kiosk experience to be viewed in a browser"
              uncheckedLabel="Allow this kiosk experience to be viewed in a browser"
            />

            <MuiBox ml={3}>
              <Switch
                checked={formData.configuration.kioskPreview?.allowCheckOut ?? false}
                disabled={!formData.configuration.kioskPreview?.enabled}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      kioskPreview: {
                        ...prev.configuration.kioskPreview,
                        allowCheckOut: e.target.checked
                      }
                    }
                  }))
                }
                checkedLabel="Allow checkout when viewed in a browser"
                uncheckedLabel="Allow checkout when viewed in a browser"
              />
            </MuiBox>

            <Switch
              checked={formData.configuration.showInKCC}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  configuration: {
                    ...prev.configuration,
                    showInKCC: e.target.checked
                  }
                }))
              }
              checkedLabel="Show this kiosk in the Kiosks list"
              uncheckedLabel="Show this kiosk in the Kiosks list"
            />
          </MuiBox>
        )}
        {areaOverlayOpen && (
          <KioskAreaOverlay
            venueId={formData.venue}
            onClose={() => {
              setAreaOverlayOpen(false);
              getAreas();
            }}
          />
        )}
        {hardwareFormOpen && (
          <KioskHardwareForm
            mode={hardwareMode}
            initialData={selectedHardware}
            onClose={() => {
              setHardwareFormOpen(false);
              setSelectedHardware(null);
            }}
            onSubmit={handleHardwareSubmit}
          />
        )}
        {hardwareOverlayOpen && (
          <KioskHardwareOverlay
            onClose={() => {
              setHardwareOverlayOpen(false);
              getHardware();
            }}
          />
        )}
        {templateOverlayOpen && (
          <KioskTemplateOverlay
            canEdit={canEditAdminAndGA}
            onClose={() => {
              setTemplateOverlayOpen(false);
            }}
          />
        )}
        {venueOverlayOpen && (
          <KioskVenueOverlay
            onClose={() => {
              setVenueOverlayOpen(false);
              getVenues();
            }}
          />
        )}
        {deleteConfirmOpen && (
          <FormDialog
            open={deleteConfirmOpen}
            cancelButtonAction={() => {
              setDeleteConfirmOpen(false);
              setSelectedHardwareToDelete(null);
            }}
            confirmButtonAction={() => {
              setFormData((prev) => ({
                ...prev,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                hardware: prev.hardware.filter((h: any) => h.id !== selectedHardwareToDelete?.id)
              }));
              setDeleteConfirmOpen(false);
              setSelectedHardwareToDelete(null);
            }}
            confirmButtonText="Delete"
            cancelButtonText="Cancel"
          >
            <Typography fontSize={24}>Are you sure you want to delete this kiosk hardware?</Typography>
          </FormDialog>
        )}
      </MuiBox>
    </FormDialog>
  );
};
